import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCw, Cpu, Globe, Loader2, ExternalLink, Check, X, ShieldAlert,
  ChevronRight, FileText, Users, Link2, Calendar, Database,
} from "lucide-react";
import { toast } from "sonner";
import {
  efAdminIntakeStats, efAdminIntakeIngestUrl, efAdminIntakeListJobs,
  efAdminIntakeJobDetail, efAdminIntakePromote, efAdminIntakeReject,
  efAdminIntakePatchCandidate,
} from "../../lib/api";

const TYPE_LABEL = {
  source:       "SOURCE",
  entity:       "ENTITY",
  relationship: "RELATIONSHIP",
  timeline:     "TIMELINE",
};
const TYPE_ICON = {
  source:       FileText,
  entity:       Users,
  relationship: Link2,
  timeline:     Calendar,
};
const STATUS_CLS = {
  candidate: "text-[#9FA8D4] border-[#9FA8D4]/40",
  edited:    "text-[#CBB892] border-[#CBB892]/40",
  promoted:  "text-[#5DD4B3] border-[#5DD4B3]/40 bg-[#0a1612]",
  rejected:  "text-[#D92D20] border-[#D92D20]/40",
};
const inputCls =
  "w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1.5 font-mono text-[11px] text-[#E4DFD3] placeholder:text-[#757065]";

export const AdminEpsteinIntake = ({ token, userToken = null }) => {
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const refreshStats = useCallback(async () => {
    try { setStats(await efAdminIntakeStats(token, userToken)); }
    catch (e) { /* silent */ }
  }, [token, userToken]);

  const refreshJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const r = await efAdminIntakeListJobs(token, { limit: 30 }, userToken);
      setJobs(r.jobs || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load jobs"); }
    finally { setLoadingJobs(false); }
  }, [token, userToken]);

  useEffect(() => { refreshStats(); refreshJobs(); }, [refreshStats, refreshJobs]);

  const ingest = async () => {
    if (!url.match(/^https?:\/\//i)) { toast.error("Enter a full http(s) URL."); return; }
    setIngesting(true);
    try {
      const r = await efAdminIntakeIngestUrl(token, { url: url.trim(), notes: notes || null }, userToken);
      toast.success(`Extracted: ${r.counts.entities} entities · ${r.counts.relationships} relationships · ${r.counts.timeline} timeline · 1 source`);
      setUrl(""); setNotes("");
      await Promise.all([refreshStats(), refreshJobs()]);
      setSelectedJob(r.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ingest failed");
    } finally { setIngesting(false); }
  };

  return (
    <div data-testid="ef-admin-intake-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <Cpu size={13} /> // AI DEPUTY · INTAKE PIPELINE V1
          </div>
          <h2 className="font-display text-2xl text-[#E4DFD3]">AI research intake</h2>
          <p className="text-[#A39E93] mt-1 text-sm max-w-3xl">
            Paste a URL. Claude Sonnet 4.5 scrapes the article and extracts neutral, evidence-bound
            candidate sources, entities, relationships, and timeline events. Nothing becomes public
            until a human reviews and promotes it — and even then, promotion lands in
            <span className="text-[#E0A526]"> &nbsp;lifecycle review&nbsp; </span>
            (non-public) where source-reliability and lifecycle gates still apply.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { refreshStats(); refreshJobs(); }}
            data-testid="ef-intake-refresh"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <RefreshCw size={11} className={(loadingJobs ? "animate-spin" : "")} /> Refresh
          </button>
        </div>
      </div>

      {/* Editorial hard-rule banner */}
      <div className="border border-[#D92D20]/40 bg-[#1a0808] px-3 py-2 mb-3 flex items-start gap-2"
           data-testid="ef-intake-hard-rule-banner">
        <ShieldAlert size={14} className="text-[#D92D20] mt-0.5" />
        <div className="text-[11px] text-[#E4DFD3] leading-relaxed">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D92D20]">EDITORIAL RULE · </span>
          AI Intake NEVER writes to the public graph. Every candidate must be human-approved and
          then move through the standard lifecycle to become public. Relationships can only be
          promoted after both endpoint entities are promoted.
        </div>
      </div>

      {/* URL input row */}
      <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
          <Globe size={11} /> // INGEST A URL
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-2">
          <div className="grid gap-2">
            <input
              data-testid="ef-intake-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/article-to-extract"
              className={inputCls}
              onKeyDown={(e) => { if (e.key === "Enter" && !ingesting) ingest(); }}
            />
            <input
              data-testid="ef-intake-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Editor notes (optional) — e.g. focus area, batch tag…"
              className={inputCls}
            />
          </div>
          <button
            data-testid="ef-intake-run-extract"
            onClick={ingest}
            disabled={ingesting || !url.trim()}
            className="bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center justify-center gap-2 min-w-[180px]">
            {ingesting ? <Loader2 size={12} className="animate-spin" /> : <Cpu size={12} />}
            {ingesting ? "Extracting…" : "Run AI extract"}
          </button>
        </div>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="ef-intake-stats">
          {Object.entries(stats.candidates_by_type || {}).map(([t, s]) => {
            const Icon = TYPE_ICON[t] || Database;
            return (
              <div key={t} className="border border-[#2D2A26] bg-[#0F0F0F] p-3"
                   data-testid={`ef-intake-stat-${t}`}>
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] flex items-center gap-1">
                  <Icon size={10} /> {TYPE_LABEL[t]}
                </div>
                <div className="font-display text-2xl text-[#E4DFD3] mt-1">{s.total || 0}</div>
                <div className="font-mono text-[9px] text-[#757065] mt-1">
                  candidate: {s.candidate || 0} · edited: {s.edited || 0} · promoted: {s.promoted || 0} · rejected: {s.rejected || 0}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Two-column: job list + detail */}
      <div className="grid lg:grid-cols-[320px_1fr] gap-3">
        <div className="border border-[#2D2A26] bg-[#0F0F0F]">
          <div className="px-3 py-2 border-b border-[#2D2A26] font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
            // INTAKE JOBS
          </div>
          <div className="max-h-[640px] overflow-y-auto" data-testid="ef-intake-jobs-list">
            {jobs.length === 0 && (
              <div className="px-3 py-6 text-center font-mono text-[10px] text-[#757065] italic">
                No intake jobs yet.
              </div>
            )}
            {jobs.map((j) => {
              const active = selectedJob === j.id;
              return (
                <button key={j.id} onClick={() => setSelectedJob(j.id)}
                  data-testid={`ef-intake-job-${j.id}`}
                  className={"w-full text-left border-b border-[#2D2A26] px-3 py-2 hover:bg-[#1a1408] " +
                    (active ? "bg-[#1a0808]" : "")}>
                  <div className="flex items-center gap-2">
                    <span className={"font-mono text-[9px] uppercase tracking-[0.3em] px-1 border " + (
                      j.status === "extracted" ? "text-[#5DD4B3] border-[#5DD4B3]/40" :
                      j.status === "failed"    ? "text-[#D92D20] border-[#D92D20]/40" :
                                                 "text-[#E0A526] border-[#E0A526]/40"
                    )}>{j.status}</span>
                    <span className="font-mono text-[9px] text-[#757065] ml-auto">
                      {j.created_at ? new Date(j.created_at).toLocaleString() : ""}
                    </span>
                  </div>
                  <div className="font-mono text-[10px] text-[#A39E93] break-all mt-1 line-clamp-2">
                    {j.url}
                  </div>
                  {j.counts && (
                    <div className="font-mono text-[9px] text-[#757065] mt-1">
                      ent {j.counts.entities || 0} · rel {j.counts.relationships || 0} · tl {j.counts.timeline || 0}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          {selectedJob ? (
            <JobDetail
              key={selectedJob}
              jobId={selectedJob}
              token={token} userToken={userToken}
              onChanged={() => { refreshStats(); refreshJobs(); }}
            />
          ) : (
            <div className="border border-dashed border-[#2D2A26] p-10 text-center font-mono text-xs uppercase tracking-widest text-[#757065]">
              Pick a job — or paste a URL above to extract a new one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const JobDetail = ({ jobId, token, userToken, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await efAdminIntakeJobDetail(token, jobId, userToken));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load job");
    } finally { setLoading(false); }
  }, [token, userToken, jobId]);
  useEffect(() => { load(); }, [load]);

  const flatCandidates = useMemo(() => {
    if (!data?.candidates) return [];
    const out = [];
    for (const t of ["source", "entity", "relationship", "timeline"]) {
      for (const c of (data.candidates[t] || [])) {
        out.push({ ...c, _candidate_type: t });
      }
    }
    return out;
  }, [data]);

  const filtered = filter === "all" ? flatCandidates :
    flatCandidates.filter((c) => c._candidate_type === filter);

  if (loading || !data) {
    return <div className="font-mono text-xs text-[#757065]">// LOADING…</div>;
  }
  const job = data.job;

  return (
    <div className="space-y-3" data-testid="ef-intake-job-detail">
      <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className={"font-mono text-[9px] uppercase tracking-[0.3em] px-1 border " + (
            job.status === "extracted" ? "text-[#5DD4B3] border-[#5DD4B3]/40" :
            job.status === "failed"    ? "text-[#D92D20] border-[#D92D20]/40" :
                                         "text-[#E0A526] border-[#E0A526]/40"
          )}>{job.status}</span>
          <span className="font-mono text-[9px] text-[#757065]">{job.id}</span>
          <span className="font-mono text-[9px] text-[#757065] ml-auto">
            {job.created_at ? new Date(job.created_at).toLocaleString() : ""}
          </span>
        </div>
        <a href={job.url} target="_blank" rel="noopener noreferrer"
          className="font-mono text-[11px] text-[#9FA8D4] hover:text-[#E4DFD3] break-all">
          {job.url} <ExternalLink size={9} className="inline" />
        </a>
        {job.scraped_title && (
          <div className="text-sm text-[#E4DFD3] mt-2">{job.scraped_title}</div>
        )}
        {job.scraped_publisher && (
          <div className="font-mono text-[10px] text-[#A39E93] mt-1">publisher: {job.scraped_publisher}</div>
        )}
        {job.error && (
          <div className="border border-[#D92D20]/40 bg-[#1a0808] mt-2 p-2 text-xs text-[#D92D20] font-mono">
            {job.error}
          </div>
        )}
        {job.counts && (
          <div className="font-mono text-[9px] text-[#757065] mt-2">
            extracted &nbsp;·&nbsp;
            {job.counts.entities || 0} entities &nbsp;·&nbsp;
            {job.counts.relationships || 0} relationships &nbsp;·&nbsp;
            {job.counts.timeline || 0} timeline &nbsp;·&nbsp;
            {job.counts.sources || 0} source
          </div>
        )}
      </div>

      {/* Type filter */}
      <div className="flex gap-1 border-b border-[#2D2A26]">
        {["all", "source", "entity", "relationship", "timeline"].map((k) => {
          const c = k === "all" ? flatCandidates.length :
            flatCandidates.filter((x) => x._candidate_type === k).length;
          const active = filter === k;
          return (
            <button key={k} onClick={() => setFilter(k)}
              data-testid={`ef-intake-filter-${k}`}
              className={"px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 -mb-px " +
                (active ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
              {k} {c ? <span className="text-[#757065]">· {c}</span> : null}
            </button>
          );
        })}
      </div>

      <div className="space-y-2" data-testid="ef-intake-candidates-list">
        {filtered.length === 0 && (
          <div className="border border-dashed border-[#2D2A26] p-6 text-center font-mono text-xs uppercase tracking-widest text-[#757065]">
            No candidates of this type.
          </div>
        )}
        {filtered.map((c) => (
          <CandidateCard key={c.id} c={c} token={token} userToken={userToken}
            onChanged={() => { load(); onChanged && onChanged(); }} />
        ))}
      </div>
    </div>
  );
};

const CandidateCard = ({ c, token, userToken, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const Icon = TYPE_ICON[c._candidate_type] || Database;

  const promote = async () => {
    setBusy(true);
    try {
      const r = await efAdminIntakePromote(token, c.id, { editor_note: null }, userToken);
      toast.success(`Promoted to ${r.live_collection} as ${r.lifecycle_status.toUpperCase()} (non-public)`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectReason || rejectReason.length < 2) { toast.error("Reason required."); return; }
    setBusy(true);
    try {
      await efAdminIntakeReject(token, c.id, { reason: rejectReason }, userToken);
      toast.success("Candidate rejected");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setBusy(false); }
  };

  const isTerminal = c.status === "promoted" || c.status === "rejected";

  return (
    <div className="border border-[#2D2A26] bg-[#0F0F0F]"
         data-testid={`ef-intake-candidate-${c.id}`}>
      <button onClick={() => setOpen(!open)}
        className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[#1a1408]"
        data-testid={`ef-intake-candidate-toggle-${c.id}`}>
        <Icon size={11} className="text-[#A39E93]" />
        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93]">
          {TYPE_LABEL[c._candidate_type]}
        </span>
        <span className={"font-mono text-[9px] uppercase tracking-[0.25em] px-1 border " + (STATUS_CLS[c.status] || "")}>
          {c.status}
        </span>
        <span className="text-sm text-[#E4DFD3] truncate">
          {c.title || c.name || c.summary || c.id}
        </span>
        <ChevronRight size={11} className={"ml-auto text-[#757065] transition " + (open ? "rotate-90" : "")} />
      </button>

      {open && (
        <div className="border-t border-[#2D2A26] px-3 py-2 space-y-2">
          {c._candidate_type === "source" && (
            <>
              <Field k="title" v={c.title} />
              <Field k="url" v={c.url} isLink />
              <Field k="publisher" v={c.publisher} />
              <Field k="source_type" v={c.source_type} />
              <Field k="published_date" v={c.published_date} />
              <Field k="summary" v={c.summary} />
            </>
          )}
          {c._candidate_type === "entity" && (
            <>
              <Field k="name" v={c.name} />
              <Field k="kind" v={c.kind} />
              <Field k="aliases" v={(c.aliases || []).join(", ")} />
              <Field k="summary" v={c.summary} />
              {c.promoted_entity_id && <Field k="→ promoted_entity_id" v={c.promoted_entity_id} />}
            </>
          )}
          {c._candidate_type === "relationship" && (
            <>
              <Field k="from" v={c.from_name} />
              <Field k="to" v={c.to_name} />
              <Field k="type" v={c.rel_type} />
              <Field k="confidence" v={c.confidence} />
              <Field k="summary" v={c.summary} />
              {c.promoted_relationship_id && <Field k="→ promoted_relationship_id" v={c.promoted_relationship_id} />}
            </>
          )}
          {c._candidate_type === "timeline" && (
            <>
              <Field k="title" v={c.title} />
              <Field k="date" v={c.date} />
              <Field k="event_type" v={c.event_type} />
              <Field k="summary" v={c.summary} />
              {c.promoted_timeline_id && <Field k="→ promoted_timeline_id" v={c.promoted_timeline_id} />}
            </>
          )}
          {c.rejected_reason && (
            <div className="font-mono text-[10px] text-[#D92D20]">rejected: {c.rejected_reason}</div>
          )}

          {!isTerminal && (
            <div className="border-t border-[#2D2A26] pt-2 flex flex-wrap items-center gap-2">
              <button onClick={promote} disabled={busy}
                data-testid={`ef-intake-promote-${c.id}`}
                className="bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-1">
                {busy ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                Promote to review
              </button>
              <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                placeholder="reject reason (≥ 2 chars)"
                data-testid={`ef-intake-reject-reason-${c.id}`}
                className={inputCls + " max-w-xs"} />
              <button onClick={reject} disabled={busy || !rejectReason}
                data-testid={`ef-intake-reject-${c.id}`}
                className="border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#D92D20] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-1">
                <X size={10} /> Reject
              </button>
              <span className="font-mono text-[9px] text-[#757065] ml-auto">
                promotion lands in <b className="text-[#E0A526]">lifecycle review</b> (non-public)
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Field = ({ k, v, isLink }) => (
  <div className="grid grid-cols-[140px_1fr] gap-2 font-mono text-[10px]">
    <div className="text-[#757065] uppercase tracking-[0.2em]">{k}</div>
    <div className="text-[#E4DFD3] break-all">
      {v ? (isLink ? (
        <a href={v} target="_blank" rel="noopener noreferrer" className="text-[#9FA8D4] hover:text-[#E4DFD3]">
          {v} <ExternalLink size={9} className="inline" />
        </a>
      ) : v) : <span className="text-[#454038] italic">—</span>}
    </div>
  </div>
);

export default AdminEpsteinIntake;
